import React, {useContext, useEffect, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {Checkbox, Popover, Switch, Text, TextInput, Toaster, useToaster} from '@gravity-ui/uikit';
import {AppContext} from 'app/context';
import {ToastVisibilityContext} from '@app/Providers/CustomToasterProvider/ToastVisibilityContext';

import './LevelPrice.scss';

const b = block('level-price');

export const LevelPrice: React.FC = () => {
    const {state: stateAppContext, emit} = useContext(AppContext);
    const {setCurrentToastName, currentToastName} = useContext(ToastVisibilityContext);
    const {priceLevelSettings: settings} = stateAppContext;

    const [input, setInput] = useState(
        settings.markupPercentage && settings.markupPercentage !== 0
            ? settings.markupPercentage.toString()
            : ''
    );
    const [isFocused, setIsFocused] = useState(false);
    const [enabled, setEnabled] = useState<boolean>(settings.enabled);
    const [checkboxes, setCheckboxes] = useState({
        displayInSearchResults: settings.displayInSearchResults,
        displayInBasket: settings.displayInBasket
    });
    const [touched, setTouched] = useState(false);
    const [edited, setEdited] = useState(false);

    const {add, remove} = useToaster();
    const toastNameRef = useRef<string | null>(null);
    const toastName = 'profile-toaster';

    const hasErrorInput = input.trim() === '';
    const hasErrorCheckBox = !checkboxes.displayInBasket && !checkboxes.displayInSearchResults;
    const hasValidationError = hasErrorInput || hasErrorCheckBox;

    const showPopover = hasValidationError;
    const showError = touched && hasValidationError;

    useEffect(() => {
        if (!edited) return;
        if (!enabled && !hasValidationError) {
            setEnabled(true);
            showToaster('enable');
        }
    }, [input, checkboxes, edited]);

    useEffect(() => {
        emit('updatePriceLevelSettings', {
            settings: {
                ...settings,
                enabled,
                displayInBasket: checkboxes.displayInBasket,
                displayInSearchResults: checkboxes.displayInSearchResults,
                markupPercentage: parseFloat(input) || 0
            }
        });
    }, [enabled, input, checkboxes]);

    function sanitize(raw: string) {
        return raw.replace(/[\s%]/g, '').replace(',', '.').replace(/б|ю/gi, '.');
    }

    function handleToggle() {
        if (enabled) {
            setEnabled(false);
            showToaster('disable');
            return;
        }

        setEdited(true);

        setTouched(true);
        if (hasValidationError) return;

        setEnabled(true);
        showToaster('enable');
    }

    function handleInputChange(value: string) {
        setEdited(true);

        const clean = sanitize(value);
        if (/^0{2,}/.test(clean) || /^0[^.]/.test(clean)) return;
        if (!/^\d*\.?\d*$/.test(clean) || clean.length > 7) return;

        setInput(clean);

        if ((clean === '' || clean === '0' || clean === '0.') && enabled) {
            setEnabled(false);
            showToaster('disable');
        }
    }

    function handleCheckboxChange(id: 'displayInSearchResults' | 'displayInBasket') {
        setEdited(true);

        setCheckboxes((prev) => {
            const next = {...prev, [id]: !prev[id]};

            if (!next.displayInBasket && !next.displayInSearchResults && enabled) {
                setEnabled(false);
                showToaster('disable');
            }
            return next;
        });
    }

    function showToaster(action: 'enable' | 'disable') {
        enum content {
            enable = 'Настройки применены',
            disable = 'Наценка отключена'
        }

        setCurrentToastName(toastName);
        toastNameRef.current = toastName;

        add({
            name: action,
            title: action === 'enable' ? content.enable : content.disable,
            theme: action === 'enable' ? 'success' : 'warning',
            autoHiding: 3000,
            isClosable: false
        });
    }

    function handleFocus() {
        setIsFocused(true);
    }
    function handleBlur() {
        setIsFocused(false);
    }

    const updateToasterStyles = () => {
        const root = document.documentElement;

        if (currentToastName === toastName) {
            root.style.setProperty('--g-toaster-top', '15%');
            root.style.setProperty('--g-toaster-visibility', 'visible');
        }
    };

    useEffect(() => {
        updateToasterStyles();
    }, [currentToastName]);

    // Удаляем тост при размонтировании/смене страницы
    useEffect(() => {
        const root = document.documentElement;

        return () => {
            if (toastNameRef.current) {
                root.style.setProperty('--g-toaster-visibility', 'hidden');
                remove(toastNameRef.current);
                toastNameRef.current = null;
            }
        };
    }, [remove]);

    return (
        <div className={b()}>
            <div className={b('header-container')}>
                <Text variant="header-1">Уровень цен</Text>
                <Popover
                    disabled={!showPopover}
                    size="s"
                    placement="top"
                    content="Введите % и выберите, где показывать наценку"
                    tooltipClassName={b('tooltip')}
                >
                    <Switch onChange={handleToggle} checked={enabled} />
                </Popover>
            </div>

            <TextInput
                size="l"
                validationState={showError && hasErrorInput ? 'invalid' : undefined}
                errorPlacement="inside"
                errorMessage="Введите процент наценки"
                className={b('input')}
                placeholder="введите % наценки"
                value={input ? (isFocused ? input : `${input} %`) : ''}
                onUpdate={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={settings.shouldDisableFields}
            />

            <Text variant="body-2" className={b('paragraph')}>
                Отображать наценку в разделах:
            </Text>

            <Checkbox
                size="l"
                className={b('checkbox', {invalid: showError && hasErrorCheckBox})}
                checked={checkboxes.displayInSearchResults}
                onChange={() => handleCheckboxChange('displayInSearchResults')}
                id="displayInSearchResults"
                disabled={settings.shouldDisableFields}
            >
                <span>результаты поиска</span>
            </Checkbox>

            <Checkbox
                size="l"
                className={b('checkbox', {invalid: showError && hasErrorCheckBox})}
                checked={checkboxes.displayInBasket}
                onChange={() => handleCheckboxChange('displayInBasket')}
                id="displayInBasket"
                disabled={settings.shouldDisableFields}
            >
                <span>корзина</span>
            </Checkbox>
        </div>
    );
};
